// =====================================================
// 白板系统 - 项目侧边栏
// =====================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import type { MouseEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PanelLeftClose, PanelLeftOpen, Plus, Search, Trash2,
  Copy, MoreVertical, FileEdit, Pencil, Download, Upload, AlertTriangle,
  Users, X,
} from 'lucide-react';
import { useWhiteboardStore } from '../stores/whiteboard-store';
import { useWhiteboardTabsStore } from '../stores/whiteboard-tabs-store';
import { ProjectDialog } from './ProjectDialog';
import * as service from '../services/whiteboard-service';
import type { WhiteboardProject } from '../types';

// =====================================================
// 确认弹窗组件
// =====================================================
function ConfirmDialog({
  isOpen, title, message, confirmText = '确定', cancelText = '取消',
  variant = 'danger', isLoading = false, onConfirm, onCancel,
}: {
  isOpen: boolean; title: string; message: string;
  confirmText?: string; cancelText?: string;
  variant?: 'danger' | 'warning';
  isLoading?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-[340px] p-5 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start gap-3 mb-4">
          <div className={`p-2 rounded-xl shrink-0 ${variant === 'danger' ? 'bg-red-50' : 'bg-amber-50'}`}>
            <AlertTriangle className={`w-5 h-5 ${variant === 'danger' ? 'text-red-500' : 'text-amber-500'}`} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">{message}</p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-4 py-1.5 text-xs font-medium text-white rounded-lg transition-colors ${variant === 'danger'
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-amber-500 hover:bg-amber-600'
              } disabled:opacity-50`}
          >
            {isLoading ? '处理中...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// 上下文菜单（自适应位置）
// =====================================================
function ContextMenu({
  project: _project, anchorRef,
  onRename, onDuplicate, onExport, onDelete, onClose,
}: {
  project: WhiteboardProject;
  anchorRef: HTMLElement | null;
  onRename: () => void; onDuplicate: () => void;
  onExport: () => void; onDelete: () => void; onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'below' | 'above'>('below');

  useEffect(() => {
    if (!anchorRef || !menuRef.current) return;
    const anchorRect = anchorRef.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    // 如果菜单底部超出视口，则向上弹出
    if (anchorRect.bottom + menuHeight + 8 > window.innerHeight) {
      setPosition('above');
    } else {
      setPosition('below');
    }
  }, [anchorRef]);

  // 点击外部关闭
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const items = [
    { icon: FileEdit, label: '重命名', onClick: onRename, className: 'text-gray-700 hover:bg-gray-50' },
    { icon: Copy, label: '创建副本', onClick: onDuplicate, className: 'text-gray-700 hover:bg-gray-50' },
    { icon: Download, label: '导出', onClick: onExport, className: 'text-gray-700 hover:bg-gray-50' },
    'divider' as const,
    { icon: Trash2, label: '删除', onClick: onDelete, className: 'text-red-600 hover:bg-red-50' },
  ];

  return (
    <div
      ref={menuRef}
      className={`absolute right-0 z-50 bg-white border border-gray-200 rounded-xl shadow-xl py-1.5 min-w-[140px] animate-in fade-in slide-in-from-top-1 duration-150 ${position === 'above' ? 'bottom-full mb-1' : 'top-full mt-1'
        }`}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item, i) => {
        if (item === 'divider') return <hr key={i} className="my-1.5 border-gray-100" />;
        const { icon: Icon, label, onClick, className } = item;
        return (
          <button
            key={label}
            onClick={onClick}
            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs font-medium transition-colors ${className}`}
          >
            <Icon className="w-3.5 h-3.5" />{label}
          </button>
        );
      })}
    </div>
  );
}

// =====================================================
// 主组件
// =====================================================
export function ProjectSidebar() {
  const navigate = useNavigate();
  const { id: currentId } = useParams<{ id: string }>();
  const {
    projects, collabProjects, isLoadingProjects, isSidebarOpen, toggleSidebar,
    loadProjects, createProject, deleteProject, duplicateProject, updateProjectMeta,
    /* KeepAlive 模式下 hasUnsavedChanges 不再用于切换检查 */
    saveCurrentCanvas, setHasUnsavedChanges,
  } = useWhiteboardStore();
  const { activeTabId, openTab, closeTab, updateTabName } = useWhiteboardTabsStore();
  const activeId = activeTabId || currentId;

  const [searchQuery, setSearchQuery] = useState('');
  const [contextMenu, setContextMenu] = useState<string | null>(null);
  const contextAnchorRef = useRef<HTMLButtonElement | null>(null);

  // 弹窗状态
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'rename' | 'duplicate'>('create');
  const [dialogLoading, setDialogLoading] = useState(false);
  const [editingProject, setEditingProject] = useState<WhiteboardProject | null>(null);

  // 删除确认弹窗
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhiteboardProject | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // 切换确认弹窗
  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [switchTargetId, setSwitchTargetId] = useState<string | null>(null);
  const [switchSaving, setSwitchSaving] = useState(false);

  useEffect(() => { loadProjects(); }, [loadProjects]);

  const filteredProjects = projects.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredCollabProjects = collabProjects.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUnpinCollab = async (event: MouseEvent, projectId: string) => {
    event.stopPropagation();
    await service.setCollaborativeProjectPinned(projectId, false);
    await loadProjects();
    if (activeId === projectId) navigate('/whiteboard');
  };

  // ===== 新建 =====
  const handleOpenCreate = () => {
    setDialogMode('create'); setEditingProject(null);
    setDialogOpen(true); setContextMenu(null);
  };

  const handleCreateConfirm = async (name: string, description: string) => {
    setDialogLoading(true);
    try {
      const project = await createProject({ name, description });
      setDialogOpen(false);
      openTab(project.id, project.name);
      navigate(`/whiteboard/${project.id}`);
    } finally { setDialogLoading(false); }
  };

  // ===== 重命名 =====
  const handleOpenRename = (project: WhiteboardProject) => {
    setDialogMode('rename'); setEditingProject(project);
    setDialogOpen(true); setContextMenu(null);
  };

  const handleRenameConfirm = async (name: string, description: string) => {
    if (!editingProject) return;
    setDialogLoading(true);
    try {
      await updateProjectMeta(editingProject.id, { name, description });
      updateTabName(editingProject.id, name);
      setDialogOpen(false);
    } finally { setDialogLoading(false); }
  };

  // ===== 切换项目（检查未保存） =====
  const handleOpen = (id: string) => {
    setContextMenu(null);
    if (id === activeId) return;
    // KeepAlive 模式下直接打开标签页，不需要检查未保存状态
    // 因为旧编辑器实例保留在内存中，不会丢失
    const allProjects = [...projects, ...collabProjects];
    const project = allProjects.find((p) => p.id === id);
    openTab(id, project?.name || '未命名白板');
    navigate(`/whiteboard/${id}`);
  };

  const handleSwitchSave = async () => {
    setSwitchSaving(true);
    try {
      if (saveCurrentCanvas) await saveCurrentCanvas();
      setSwitchDialogOpen(false);
      if (switchTargetId) navigate(`/whiteboard/${switchTargetId}`);
    } finally {
      setSwitchSaving(false);
      setSwitchTargetId(null);
    }
  };

  const handleSwitchDiscard = () => {
    setHasUnsavedChanges(false);
    setSwitchDialogOpen(false);
    if (switchTargetId) navigate(`/whiteboard/${switchTargetId}`);
    setSwitchTargetId(null);
  };

  // ===== 删除（页面内弹窗） =====
  const handleDeleteClick = (project: WhiteboardProject) => {
    setDeleteTarget(project); setDeleteDialogOpen(true); setContextMenu(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await deleteProject(deleteTarget.id);
      const nextId = closeTab(deleteTarget.id);
      if (currentId === deleteTarget.id) {
        navigate(nextId ? `/whiteboard/${nextId}` : '/whiteboard');
      }
    } finally {
      setDeleteLoading(false); setDeleteDialogOpen(false); setDeleteTarget(null);
    }
  };

  // ===== 创建副本（弹窗先命名） =====
  const handleOpenDuplicate = (project: WhiteboardProject) => {
    setDialogMode('duplicate');
    setEditingProject(project);
    setDialogOpen(true);
    setContextMenu(null);
  };

  const handleDuplicateConfirm = async (name: string, description: string) => {
    if (!editingProject) return;
    setDialogLoading(true);
    try {
      // 复制项目并重命名
      const newProject = await duplicateProject(editingProject.id);
      await updateProjectMeta(newProject.id, { name, description });
      setDialogOpen(false);
      openTab(newProject.id, name);
      navigate(`/whiteboard/${newProject.id}`);
    } finally { setDialogLoading(false); }
  };

  // ===== 导入本地 .excalidraw 文件 =====
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.excalidraw,.json';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        const name = file.name.replace(/\.(excalidraw|json)$/, '') || '导入的白板';

        // 创建新项目
        const project = await createProject({ name });

        // 写入画布数据
        await updateProjectMeta(project.id, {
          canvas_data: {
            elements: data.elements || [],
            appState: data.appState || {},
          },
        });

        // 如果包含图片，上传到 Storage
        if (data.files && Object.keys(data.files).length > 0 && project.storage_key) {
          const fileEntries = Object.entries(data.files) as [string, any][];
          await Promise.all(
            fileEntries.map(async ([id, fileData]) => {
              try {
                if (!fileData.dataURL) return;
                const match = fileData.dataURL.match(/^data:([^;]+);base64,(.+)$/);
                if (!match) return;
                const binary = atob(match[2]);
                const bytes = new Uint8Array(binary.length);
                for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                await service.uploadFileToStorage(project.storage_key, id, bytes, match[1]);
              } catch (err) {
                console.error(`Import file ${id} upload error:`, err);
              }
            })
          );
        }

        navigate(`/whiteboard/${project.id}`);
      } catch (err) {
        console.error('Import error:', err);
        alert('导入失败，请确保文件是有效的 .excalidraw 格式');
      }
    };
    input.click();
  };

  const handleExport = async (project: WhiteboardProject) => {
    setContextMenu(null);
    try {
      const { getCanvasExportData } = useWhiteboardStore.getState();

      let elements: any[] = [];
      let appState: any = {};
      let files: Record<string, any> = {};

      if (project.id === activeId && getCanvasExportData) {
        // 当前打开的项目 → 直接从 Excalidraw API 取活跃数据（含内存中的图片）
        const liveData = getCanvasExportData();
        elements = liveData.elements;
        appState = liveData.appState;
        files = liveData.files;
      } else {
        // 其他项目 → 从 DB 拉最新数据 + Storage 下载图片
        const freshProject = await service.fetchProjectById(project.id);
        if (!freshProject) return;
        const canvasData = service.loadElements(freshProject);
        elements = canvasData.elements || [];
        appState = canvasData.appState || {};
        if (freshProject.storage_key && elements.length) {
          files = await service.loadFiles(freshProject.storage_key, elements);
        }
      }

      const exportData = { type: 'excalidraw', version: 2, elements, appState, files };
      const json = JSON.stringify(exportData, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${project.name}.excalidraw`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { console.error('Export error:', err); }
  };

  const handleCloseMenu = useCallback(() => setContextMenu(null), []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return '刚刚';
    if (diffMins < 60) return `${diffMins}分钟前`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}小时前`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  // ===== 公共弹窗 =====
  const dialogs = (
    <>
      <ProjectDialog
        isOpen={dialogOpen} mode={dialogMode}
        initialName={dialogMode === 'duplicate' ? `${editingProject?.name || ''} (副本)` : editingProject?.name || ''} initialDescription={editingProject?.description || ''}
        onConfirm={dialogMode === 'create' ? handleCreateConfirm : dialogMode === 'duplicate' ? handleDuplicateConfirm : handleRenameConfirm}
        onCancel={() => setDialogOpen(false)} isLoading={dialogLoading}
      />
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        title="删除白板"
        message={`确定要删除「${deleteTarget?.name || ''}」吗？删除后将无法恢复。`}
        confirmText="删除" variant="danger"
        isLoading={deleteLoading}
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteDialogOpen(false); setDeleteTarget(null); }}
      />
      {/* 切换确认弹窗 */}
      {switchDialogOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => { setSwitchDialogOpen(false); setSwitchTargetId(null); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-[360px] p-5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 rounded-xl shrink-0 bg-amber-50">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">未保存的更改</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">当前白板有未保存的更改，是否在切换前保存？</p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setSwitchDialogOpen(false); setSwitchTargetId(null); }}
                className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSwitchDiscard}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                不保存
              </button>
              <button
                onClick={handleSwitchSave}
                disabled={switchSaving}
                className="px-4 py-1.5 text-xs font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50"
              >
                {switchSaving ? '保存中...' : '保存并切换'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // 折叠态
  if (!isSidebarOpen) {
    return (
      <div className="w-10 border-r border-gray-200 bg-white flex flex-col items-center py-3 gap-2">
        <button onClick={toggleSidebar} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="展开侧边栏">
          <PanelLeftOpen className="w-4 h-4" />
        </button>
        <button onClick={handleOpenCreate} className="p-1.5 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors" title="新建白板">
          <Plus className="w-4 h-4" />
        </button>
        {dialogs}
      </div>
    );
  }

  return (
    <div className="w-64 border-r border-gray-200 bg-white flex flex-col h-full">
      {dialogs}

      {/* 头部 */}
      <div className="p-3 border-b border-gray-100 flex items-center justify-between">
        <button onClick={() => navigate('/whiteboard')} className="flex items-center gap-2 hover:opacity-70 transition-opacity" title="返回所有白板">
          <Pencil className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold text-gray-800">我的白板</span>
        </button>
        <div className="flex items-center gap-1">
          <button onClick={handleImport} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="导入白板">
            <Upload className="w-4 h-4" />
          </button>
          <button onClick={handleOpenCreate} className="p-1.5 rounded-lg text-blue-500 hover:text-blue-700 hover:bg-blue-50 transition-colors" title="新建白板">
            <Plus className="w-4 h-4" />
          </button>
          <button onClick={toggleSidebar} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors" title="收起侧边栏">
            <PanelLeftClose className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 搜索 */}
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input type="text" placeholder="搜索白板..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-gray-50" />
        </div>
      </div>

      {/* 项目列表 */}
      <div className="flex-1 overflow-y-auto px-2 py-1">
        {isLoadingProjects ? (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length === 0 && filteredCollabProjects.length === 0 ? (
          <div className="text-center py-8">
            <FileEdit className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-xs text-gray-400">{searchQuery ? '没有匹配的白板' : '还没有白板'}</p>
            {!searchQuery && (
              <button onClick={handleOpenCreate} className="mt-2 text-xs text-blue-500 hover:text-blue-700">创建第一个白板</button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <section>
              <div className="px-3 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">我的白板</div>
              <div className="space-y-0.5">
              {filteredProjects.map((project) => (
              <div
                key={project.id}
                className={`group relative rounded-lg px-3 py-2 cursor-pointer transition-colors ${activeId === project.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                onClick={() => handleOpen(project.id)}
              >
                <div className="flex items-center gap-1.5 pr-6 min-w-0">
                  <div className="text-sm font-medium truncate min-w-0">{project.name}</div>
                  {project.share_mode === 'edit' && (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700"
                      title="该白板已开启协作"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      协作
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(project.updated_at)}</div>

                {/* 更多菜单按钮 */}
                <button
                  ref={(el) => { if (contextMenu === project.id) contextAnchorRef.current = el; }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setContextMenu(contextMenu === project.id ? null : project.id);
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-all"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>

                {/* 上下文菜单 */}
                {contextMenu === project.id && (
                  <ContextMenu
                    project={project}
                    anchorRef={contextAnchorRef.current}
                    onRename={() => handleOpenRename(project)}
                    onDuplicate={() => handleOpenDuplicate(project)}
                    onExport={() => handleExport(project)}
                    onDelete={() => handleDeleteClick(project)}
                    onClose={handleCloseMenu}
                  />
                )}
              </div>
              ))}
              {filteredProjects.length === 0 && (
                <p className="px-3 py-2 text-[11px] text-gray-400">没有匹配的自建白板</p>
              )}
              </div>
            </section>

            {filteredCollabProjects.length > 0 && (
              <section>
                <div className="px-3 pb-1 flex items-center gap-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  <Users className="w-3 h-3" /> 与我协作
                </div>
                <div className="space-y-0.5">
                  {filteredCollabProjects.map((project) => (
                    <div
                      key={project.id}
                      className={`group relative rounded-lg px-3 py-2 cursor-pointer transition-colors ${activeId === project.id ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-gray-50 text-gray-700'}`}
                      onClick={() => handleOpen(project.id)}
                    >
                      <div className="text-sm font-medium truncate pr-6">{project.name}</div>
                      <div className="text-[10px] text-gray-400 mt-0.5">{formatDate(project.updated_at)}</div>
                      <button
                        onClick={(event) => void handleUnpinCollab(event, project.id)}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-200 transition-all"
                        title="从与我协作中移除"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
